import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, ArrowLeft, Shield, Users, FileText, Calendar, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { TeamCostDisplay } from "@/components/common/TeamCostDisplay";
import { useTeamMedianTrietia } from "@/hooks/useTeamMedianTrietia";
import { useTeamMedianHourlyCost } from "@/hooks/useTeamMedianHourlyCost";
import { useTeamEquipmentTotal } from "@/hooks/useTeamEquipmentTotal";
import { useTeamGuardsEquipmentTotal } from "@/hooks/useTeamGuardsEquipmentTotal";

interface Team {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  created_at: string;
  updated_at: string;
}

const TeamDetail = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [team, setTeam] = useState<Team | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [members, setMembers] = useState<number>(0);
  const [reports, setReports] = useState<number>(0);
  const [missions, setMissions] = useState<number>(0);
  const [applications, setApplications] = useState<number>(0);

  // Fetch team statistics
  const { data: medianTrietia } = useTeamMedianTrietia(teamId);
  const { data: medianHourlyCost } = useTeamMedianHourlyCost(teamId, medianTrietia ?? null);
  const { data: teamEquipmentTotal } = useTeamEquipmentTotal(teamId);
  const { data: guardsEquipmentTotal } = useTeamGuardsEquipmentTotal(teamId);

  useEffect(() => {
    const fetchTeamData = async () => {
      if (!teamId) return;
      
      setIsLoading(true);
      try {
        // Fetch team details
        const { data: teamData, error } = await supabase
          .from('teams')
          .select('*')
          .eq('id', teamId)
          .single();
          
        if (error) throw error;
        
        setTeam(teamData);
        
        // Fetch team members count
        const { count: membersCount, error: membersError } = await supabase
          .from('team_members')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', teamId);
          
        if (membersError) throw membersError;
        
        setMembers(membersCount || 0);
        
        // Fetch reports count
        const { count: reportsCount, error: reportsError } = await supabase
          .from('reports')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', teamId);
          
        if (reportsError) throw reportsError;
        
        setReports(reportsCount || 0);
        
        // Fetch missions count
        const { count: missionsCount, error: missionsError } = await supabase
          .from('missions')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', teamId);
          
        if (missionsError) throw missionsError;
        
        setMissions(missionsCount || 0);

        // Fetch applications count
        const { count: applicationsCount, error: applicationsError } = await supabase
          .from('applications')
          .select('*', { count: 'exact', head: true })
          .eq('team_id', teamId);
          
        if (applicationsError) throw applicationsError;
        
        setApplications(applicationsCount || 0);
        
      } catch (error: any) {
        console.error("Error fetching team:", error);
        toast({
          title: "Error",
          description: "Failed to load team data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTeamData();
  }, [teamId, toast]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            className="flex items-center gap-2"
            onClick={() => navigate('/teams')}
          >
            <ArrowLeft size={16} />
            Back to Teams
          </Button>
          
          <Skeleton className="h-12 w-1/3" />
          <Skeleton className="h-6 w-2/3" />
          
          <Card className="mt-8">
            <CardContent className="p-6">
              <Skeleton className="h-48 w-full" />
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  if (!team) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            className="flex items-center gap-2"
            onClick={() => navigate('/teams')}
          >
            <ArrowLeft size={16} />
            Back to Teams
          </Button>
          
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Team not found. The team you're looking for may have been removed or you don't have permission to view it.
            </AlertDescription>
          </Alert>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <Button 
          variant="ghost" 
          className="flex items-center gap-2"
          onClick={() => navigate('/teams')}
        >
          <ArrowLeft size={16} />
          Back to Teams
        </Button>
        
        <div className="space-y-2">
          <h1 className="text-3xl font-bold">{team.name}</h1>
          <p className="text-gray-500">{team.description || "No description provided"}</p>
        </div>
        
        <Card className="overflow-hidden">
          <div 
            className="h-48 bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center"
            style={{
              backgroundImage: team.image_url 
                ? `linear-gradient(rgba(59, 130, 246, 0.7), rgba(29, 78, 216, 0.7)), url(${team.image_url})` 
                : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <Shield className="h-20 w-20 text-white opacity-80" />
          </div>
          
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <Users className="h-6 w-6 text-blue-600 mb-2" />
                <span className="text-lg font-semibold">{members}</span>
                <span className="text-sm text-gray-500">Members</span>
              </div>
              
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600 mb-2" />
                <span className="text-lg font-semibold">{reports}</span>
                <span className="text-sm text-gray-500">Reports</span>
              </div>
              
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600 mb-2" />
                <span className="text-lg font-semibold">{missions}</span>
                <span className="text-sm text-gray-500">Missions</span>
              </div>
              
              <div className="flex flex-col items-center p-4 bg-gray-50 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600 mb-2" />
                <span className="text-lg font-semibold">{applications}</span>
                <span className="text-sm text-gray-500">Applications</span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <TeamCostDisplay
                cost={medianHourlyCost}
                tooltipText="Median hourly cost based on team's median τριετία"
                displayLabel="Median €/h"
                borderClass="border-blue-500"
                textClass="text-blue-700"
                isCurrency={true}
              />

              <TeamCostDisplay
                cost={teamEquipmentTotal}
                tooltipText="Total value of equipment assigned to team"
                displayLabel="Team Equip"
                borderClass="border-green-500"
                textClass="text-green-700"
                isCurrency={true}
              />

              <TeamCostDisplay
                cost={guardsEquipmentTotal}
                tooltipText="Total value of equipment assigned to team guards"
                displayLabel="Guards Equip"
                borderClass="border-purple-500"
                textClass="text-purple-700"
                isCurrency={true}
              />

              <TeamCostDisplay
                cost={medianTrietia}
                tooltipText="Median τριετία level of team members"
                displayLabel="Μέση Τριετία"
                borderClass="border-amber-500"
                textClass="text-amber-700"
                isCurrency={false}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Link to={`/reports/team/${team.id}`} className="w-full">
                <Button className="w-full" variant="outline">Reports</Button>
              </Link>
              
              <Link to={`/missions/team/${team.id}`} className="w-full">
                <Button className="w-full" variant="outline">Missions</Button>
              </Link>

              <Link to={`/applications?team=${team.id}`} className="w-full">
                <Button className="w-full" variant="outline">Applications</Button>
              </Link>
              
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate(`/applications/team/${team.id}/chat`)}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default TeamDetail;
